// Éléments communs des écrans « Contrats de maintenance » : gardes d'accès, badges de statut, libellés.
import type { ReactNode } from 'react';
import { LIBELLES_STATUT_CONTRAT, LIBELLES_STATUT_VISITE, type StatutContrat, type StatutVisite } from '@shared/contrats/contrats.ts';
import { useSession } from '../../lib/session.tsx';
import { useModulesEtat } from '../../lib/modules.ts';
import { Badge, Chargement } from '../../components/ui.tsx';
import { PageIntrouvable } from '../legal/PageIntrouvable.tsx';

/** Écrans des contrats : option active + lecture + rôle d'encadrement (le technicien ne voit jamais un contrat). */
export function GardeContrats({ children }: { children: ReactNode }) {
  const { charge, modules } = useModulesEtat();
  const { peut } = useSession();
  if (!charge) return <Chargement />;
  if (!modules.contrats_maintenance || !peut('contrats.lire') || !(peut('contrats.ecrire') || peut('visites.planifier'))) return <PageIntrouvable />;
  return <>{children}</>;
}

/** Écrans des visites : option active + lecture (le technicien n'y voit que les visites qui lui sont affectées). */
export function GardeVisites({ children }: { children: ReactNode }) {
  const { charge, modules } = useModulesEtat();
  const { peut } = useSession();
  if (!charge) return <Chargement />;
  if (!modules.contrats_maintenance || !peut('contrats.lire')) return <PageIntrouvable />;
  return <>{children}</>;
}

const TON_CONTRAT: Record<StatutContrat, 'neutre' | 'accent' | 'ambre' | 'danger' | 'succes' | 'info'> = {
  brouillon: 'ambre', signe: 'info', en_cours: 'succes', a_renouveler: 'ambre', resilie: 'danger', termine: 'neutre', remplace: 'neutre',
};
export function BadgeStatutContrat({ statut }: { statut: StatutContrat }) {
  return <Badge ton={TON_CONTRAT[statut]}>{LIBELLES_STATUT_CONTRAT[statut]}</Badge>;
}

const TON_VISITE: Record<StatutVisite, 'neutre' | 'accent' | 'ambre' | 'danger' | 'succes' | 'info'> = {
  a_planifier: 'ambre', planifiee: 'info', en_cours: 'accent', realisee: 'succes', annulee: 'neutre',
};
export function BadgeStatutVisite({ statut, enRetard }: { statut: StatutVisite; enRetard?: boolean }) {
  return (
    <>
      <Badge ton={TON_VISITE[statut]}>{LIBELLES_STATUT_VISITE[statut]}</Badge>
      {enRetard && <Badge ton="danger">En retard</Badge>}
    </>
  );
}

export const CRENEAUX_LIBELLES: Record<'journee' | 'matin' | 'apres_midi', string> = { journee: 'Journée', matin: 'Matin', apres_midi: 'Après-midi' };

/** Montant saisi (« 1 200,50 ») → chaîne envoyée à l'API, ou null si vide. */
export const montantSaisi = (v: string) => (v.trim() === '' ? null : v.replace(/\s/g, '').replace(',', '.'));
